const { APP_DESTINATIONS, APP_BY_DOSSIER, APPS } = require("../types/apps");

const isBrowser = typeof window !== "undefined";


/**
 * Browser: API origin. Uses REACT_APP_API_PORT when it is set (dev: React on
 * 8080, API on 8008). When it is empty the API is served by the same server as
 * the page, so we keep window.location.host (hostname + port). Building it from
 * window.location.hostname alone drops the port, e.g. develop.welda.be:8008.
 */
const getBrowserApiOrigin = ({ protocol = "https" }) => {
  const apiPort = String(process.env.REACT_APP_API_PORT || "").trim();
  const host = apiPort
    ? `${window.location.hostname}:${apiPort}`
    : window.location.host;
  return `${protocol}://${host}`;
};

// Hostnames that sit behind the public 443 proxy. A "production" build on any
// other host (develop.welda.be, request.welda.be, ...) is served by Node itself
// on PORT, so its links must keep that host and port.
const PUBLIC_HOSTNAMES = new Set([
  "awtech.be",
  ...Object.values(APPS).map((app) => app.publicUrl.split("/")[0]),
]);

/**
 * REACT_APP_ENV / NODE_ENV, except that "production" on a non-public host is
 * handled like "test": links stay on the current host and port instead of
 * pointing to portal.welda.be or dropping the port.
 */
const getUrlEnv = () => {
  const env = isBrowser ? process.env.REACT_APP_ENV : process.env.NODE_ENV;
  if (env !== "production") return env;
  const hostname = isBrowser ? window.location.hostname : process.env.HOST;
  if (hostname && !PUBLIC_HOSTNAMES.has(hostname)) return "test";
  return env;
};

const getBaseOrigin = ({ protocol = "https" }) => {
  let origin = null;
  if (isBrowser) {
    origin = window.location.origin;
  } else {
    origin = `${protocol}://${process.env.HOST}:${process.env.PORT}`;
  }
  return origin;
};

/* ===============================
   API / Server
================================ */

const getServerUrl = ({ protocol = "https", suffix = "", dossierNr = null, mockBrowser = false } = {}) => {
  const origin = getBaseOrigin({ protocol });
  if (!origin) return null;
  let serverUrl = null;

  const env = getUrlEnv();

  if (isBrowser || mockBrowser) {
    // Same for every env; an unknown REACT_APP_ENV must not yield "null/api".
    serverUrl = isBrowser
      ? `${getBrowserApiOrigin({ protocol })}${suffix}`
      : `${protocol}://mockhostname:${process.env.PORT}${suffix}`;
  } else {
    switch (env) {
      case "production": {
        // Prefer dossier public URL when provided; otherwise fall back to HOST.
        // Without this, missing dossierNr becomes the literal host "undefined".
        const publicUrl = APP_BY_DOSSIER[dossierNr]?.publicUrl;
        const host = publicUrl || process.env.HOST;
        serverUrl = host ? `${protocol}://${host}${suffix}` : null;
        break;
      }
      case "development":
        serverUrl = `${origin}${suffix}`;
        break;
      case "test":
        serverUrl = `${origin}${suffix}`;
        break;
    }
  }
  // console.info("getServerUrl", isBrowser, protocol, suffix, dossierNr, env, serverUrl);
  return serverUrl;
};

const getServerApiUrl = () => {
  const serverApiUrl = getServerUrl({ suffix: "/api" });
  return serverApiUrl;
};

/* ===============================
   WebSocket
================================ */

const getWebsocketUrl = () => {
  const websocketUrl = getServerUrl({ protocol: "wss", suffix: "" });
  return websocketUrl;
};

const getFrontendUrl = ({ dossierNr = null, destination, mockBrowser = false, isRelative = false }) => {

  const env = getUrlEnv();
  const protocol = "https";
  const destinationPrefix = APP_DESTINATIONS[destination].PREFIX;

  let frontendUrl = null;
  if (isBrowser || mockBrowser) {
    const origin = getBaseOrigin({ protocol: "https" });
    if (!origin) return null;
    switch (destination) {
      case APP_DESTINATIONS.SITE.KEY:
        switch (env) {
          case "production":
            frontendUrl = `${protocol}://${APP_BY_DOSSIER[dossierNr]?.publicUrl}`;
            break;
          case "development":
            frontendUrl = `${isRelative ? "" : origin}${destinationPrefix}/${dossierNr}`;
            break;
          case "test":
            frontendUrl = `${isRelative ? "" : origin}${destinationPrefix}/${dossierNr}`;
            break;
        }
        break;
      case APP_DESTINATIONS.INTRANET.KEY:
        switch (env) {
          case "production":
            console.info("production");
            frontendUrl = `${isRelative ? "" : `${protocol}://${APPS.INTRANET.publicUrl}`}${destinationPrefix}`;
            break;
          case "development":
            frontendUrl = `${isRelative ? "" : origin}${destinationPrefix}`;
            break;
          case "test":
            frontendUrl = `${isRelative ? "" : origin}${destinationPrefix}`;
            break;
        }
        break;

      case APP_DESTINATIONS.STATIC.KEY:
        switch (env) {
          case "production":
            frontendUrl = `${protocol}://${APP_BY_DOSSIER[dossierNr]?.publicUrl}${destinationPrefix}`;
            break;
          case "development":
            frontendUrl = `${isRelative ? "" : origin}`;
            break;
          case "test":
            frontendUrl = `${isRelative ? "" : origin}`;
            break;
        }
        break;

      case APP_DESTINATIONS.ROOT.KEY:
        switch (env) {
          case "production":
            frontendUrl = `${protocol}://${APP_BY_DOSSIER[dossierNr]?.publicUrl}${destinationPrefix}`;
            break;
          case "development":
            frontendUrl = `${isRelative ? "" : origin}`;
            break;
          case "test":
            frontendUrl = `${isRelative ? "" : origin}`;
            break;
        }
        break;
    }
  } else {
    const hostname = process.env.HOST;
    let port = null
    if (env === "development") {
      port = process.env.PORT_FRONT_DEV;
    } else if (process.env.NODE_ENV === "production") {
      // Production build on a non-public host: Node serves frontend/build on PORT.
      port = env === "test" ? process.env.PORT : null;
    } else if (env === "test") {
      // In test the backend serves frontend/build itself (expressApp.js), so the
      // frontend lives on PORT when no separate frontend port is configured.
      port = process.env.PORT_FRONT_TEST || process.env.PORT;
    }
    // NOTE: In production the frontend is served on the default https port (443),
    // so we omit the port entirely. This keeps server-generated INTRANET-destination
    // links clean (e.g. portal.welda.be/... instead of portal.welda.be:8008/...).
    const url = isRelative
      ? ""
      : `${protocol}://${hostname}${port ? `:${port}` : ""}`;

    switch (destination) {
      case APP_DESTINATIONS.SITE.KEY:
        switch (env) {
          case "production":
            frontendUrl = `${protocol}://${APP_BY_DOSSIER[dossierNr]?.publicUrl}`;
            break;
          case "development":
            frontendUrl = `${url}${destinationPrefix}/${dossierNr}`;
            break;
          case "test":
            frontendUrl = `${url}${destinationPrefix}/${dossierNr}`;
            break;
        }
        break;
      case APP_DESTINATIONS.INTRANET.KEY:
        switch (env) {
          case "production":
            frontendUrl = `${url}${destinationPrefix}`;
            break;
          case "development":
            frontendUrl = `${url}${destinationPrefix}`;
            break;
          case "test":
            frontendUrl = `${url}${destinationPrefix}`;
            break;
        }
        break;

      case APP_DESTINATIONS.STATIC.KEY:
        switch (env) {
          case "production":
            frontendUrl = `${protocol}://${APP_BY_DOSSIER[dossierNr]?.publicUrl}${destinationPrefix}`;
            break;
          case "development":
            frontendUrl = `${url}`;
            break;
          case "test":
            frontendUrl = `${url}`;
            break;
        }
        break;

      case APP_DESTINATIONS.ROOT.KEY:
        switch (env) {
          case "production":
            frontendUrl = `${protocol}://${APP_BY_DOSSIER[dossierNr]?.publicUrl}${destinationPrefix}`;
            break;
          case "development":
            frontendUrl = `${url}`;
            break;
          case "test":
            frontendUrl = `${url}`;
            break;
        }
        break;

    }
  }
  if (frontendUrl === null || frontendUrl === undefined) {
    return null;
  }
  // console.info("getFrontendUrl", dossierNr, destination, isBrowser, frontendUrl);
  return frontendUrl;
};

// Log all urls combinations for all destinations
const logAllUrlsCombinations = () => {
  const dossierNrs = Object.keys(APP_BY_DOSSIER);
  const mockBrowsers = [true, false];
  const isRelatives = [true, false];

  // FRONTEND URLS COMBINATIONS
  const destinations = Object.keys(APP_DESTINATIONS);
  const frontendUrlsCombinations = [];
  for (const destination of destinations) {
    for (const dossierNr of dossierNrs) {
      for (const mockBrowser of mockBrowsers) {
        for (const isRelative of isRelatives) {
          const url = getFrontendUrl({ dossierNr, destination, mockBrowser, isRelative });
          frontendUrlsCombinations.push(`${dossierNr} - ${destination} - ${mockBrowser ? "from browser" : "from server"} - ${isRelative ? "relative" : "absolute"} - ${url}`);
        }
      }
    }
  }

  // SERVER URLS COMBINATIONS
  const serverUrlsCombinations = [];
  for (const dossierNr of dossierNrs) {
    for (const mockBrowser of mockBrowsers) {
      const url = getServerUrl({ dossierNr, mockBrowser });
      serverUrlsCombinations.push(`${dossierNr} - ${mockBrowser ? "from browser" : "from server"} - ${url}`);
    }
  }

  // console.info(">>>LOGGING ALL URLS COMBINATIONS<<<");
  // console.info("getFrontendUrl combinations>>>");
  // console.info(frontendUrlsCombinations.join("\n"));
  // console.info("getServerUrl combinations>>>");
  // console.info(serverUrlsCombinations.join("\n"));
};
if (!isBrowser) {
  logAllUrlsCombinations();
}
module.exports = { getFrontendUrl, getServerUrl, getServerApiUrl, getWebsocketUrl };
